// src/components/UI.tsx
import React, {
  CSSProperties,
  ReactNode,
  InputHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  ElementType,
  forwardRef,
} from "react";

// ─── Theme ─────────────────────────────────────────────────────────────────────
export const theme = {
  colors: {
    // Berry‑inspired palette
    primary: "#7367F0", // purple
    primarySoft: "#F3F0FF",
    blue: "#26A4FF",
    amber: "#FFB547",
    secondary: "#F4F5FD",
    secondaryBorder: "#E3E7F7",
    text: "#1A2240",
    muted: "#6B7280",
    background: "#F5F7FB",
    surface: "#FFFFFF",
    danger: "#DC2626",
    success: "#10B981",
    successSoft: "#DCFCE7",
  },
  spacing: { xs: 4, sm: 8, md: 16, lg: 24, xl: 32 },
  borderRadius: 12,
};

// ─── Core Styles ────────────────────────────────────────────────────────────────
const styles: Record<string, CSSProperties> = {
  // ── Buttons ───────────────────────────────────────────────
  buttonPrimary: {
    backgroundColor: theme.colors.primary,
    color: theme.colors.surface,
    border: "none",
    borderRadius: theme.borderRadius,
    padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
    cursor: "pointer",
    fontSize: "0.95rem",
    fontWeight: 500,
  },
  buttonSecondary: {
    backgroundColor: theme.colors.secondary,
    color: theme.colors.text,
    border: `1px solid ${theme.colors.secondaryBorder}`,
    borderRadius: theme.borderRadius,
    padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
    cursor: "pointer",
    fontSize: "0.95rem",
    fontWeight: 500,
  },

  // ── SearchInput ──────────────────────────────────────────
  searchInput: {
    width: "100%",
    padding: `${theme.spacing.sm}px 0`,
    border: "none",
    borderRadius: theme.borderRadius,
    fontSize: "0.95rem",
    color: theme.colors.text,
    boxSizing: "border-box",
    background: "transparent",
    outline: "none",
  },

  // ── Header ───────────────────────────────────────────────
  header: {
    fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
    color: theme.colors.text,
    margin: `${theme.spacing.md}px 0 ${theme.spacing.sm}px`,
    fontWeight: 600,
  },

  // ── Card ─────────────────────────────────────────────────
  card: {
    backgroundColor: theme.colors.surface,
    border: `1px solid ${theme.colors.secondaryBorder}`,
    borderRadius: theme.borderRadius * 1.5,
    padding: theme.spacing.lg,
    boxSizing: "border-box",
    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.06)",
    position: "relative",
  },

  // ── List ─────────────────────────────────────────────────
  list: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  listItem: {
    padding: `${theme.spacing.xs}px 0`,
    borderBottom: `1px solid ${theme.colors.secondaryBorder}`,
    color: theme.colors.text,
    fontSize: "0.95rem",
  },

  // ── StatBubble ──────────────────────────────────────────
  statBubble: {
    backgroundColor: theme.colors.surface,
    border: `1px solid ${theme.colors.secondaryBorder}`,
    borderRadius: theme.borderRadius * 1.5,
    padding: theme.spacing.md,
    textAlign: "center" as const,
    flex: "1 1 120px",
    margin: theme.spacing.xs,
    boxSizing: "border-box",
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: theme.colors.primary,
  },
  statLabel: {
    fontSize: "0.85rem",
    color: theme.colors.muted,
    marginTop: theme.spacing.xs,
  },

  // ── Form controls shared base ───────────────────────────
  inputBase: {
    width: "100%",
    padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
    border: `1px solid ${theme.colors.secondaryBorder}`,
    borderRadius: theme.borderRadius,
    boxSizing: "border-box",
    fontSize: "0.95rem",
    color: theme.colors.text,
    background: theme.colors.surface,
  },
  fieldWrapper: {
    marginBottom: theme.spacing.md,
  },
  fieldLabel: {
    display: "block",
    marginBottom: theme.spacing.xs,
    fontSize: "0.95rem",
    fontWeight: 500,
    color: theme.colors.text,
  },

  // ── MetricCard ──────────────────────────────────────────
  metricCard: {
    borderRadius: 20,
    padding: theme.spacing.lg,
    boxShadow: "0 20px 55px rgba(79, 70, 229, 0.35)",
    color: "#FFFFFF",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minWidth: 0,
    position: "relative",
    overflow: "hidden",
  },
  metricCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing.sm,
  },
  metricLabel: {
    fontSize: 12,
    fontWeight: 500,
    opacity: 0.9,
    letterSpacing: 0.3,
    textTransform: "uppercase",
  },
  metricValue: {
    fontSize: 28,
    fontWeight: 700,
    marginTop: theme.spacing.sm,
  },
  metricHelper: {
    fontSize: 12,
    marginTop: theme.spacing.sm,
    opacity: 0.95,
  },
  metricRightLabel: {
    fontSize: 12,
    opacity: 0.95,
  },
  metricIconBadge: {
    width: 32,
    height: 32,
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    background: "rgba(255,255,255,0.18)",
  },

  // ── ListPanel ───────────────────────────────────────────
  listPanel: {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius * 1.5,
    padding: theme.spacing.lg,
    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.06)",
    border: `1px solid ${theme.colors.secondaryBorder}`,
    boxSizing: "border-box",
  },
  listPanelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing.sm,
  },
  listPanelTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.colors.text,
  },
  listPanelBody: {
    marginTop: theme.spacing.sm,
  },
  listPanelRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${theme.spacing.xs}px 0`,
    borderBottom: `1px solid ${theme.colors.secondaryBorder}`,
  },
  listPanelPrimary: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.colors.text,
  },
  listPanelSecondary: {
    fontSize: 12,
    color: theme.colors.muted,
    marginTop: 2,
  },
  listPanelMeta: {
    fontSize: 12,
    fontWeight: 600,
  },
  listPanelEmpty: {
    fontSize: 12,
    color: theme.colors.muted,
  },
  listPanelRightPositive: {
    color: "#16A34A",
  },
  listPanelRightNegative: {
    color: theme.colors.danger,
  },
};

// ────────────────────────────────────────────────────────────────────────────────

// ─── Button ───────────────────────────────────────────────────────────────────
export interface ButtonProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary";
  style?: CSSProperties;
  className?: string;
  disabled?: boolean;
  buttonRef?: React.Ref<HTMLButtonElement>;
  type?: "button" | "submit" | "reset"; // NEW
}
export const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  variant = "primary",
  style,
  className,
  disabled = false,
  buttonRef,
  type = "button", // NEW default
}) => {
  const variantClass =
    variant === "primary" ? "ps-button--primary" : "ps-button--secondary";
  const buttonClassName = ["ps-button", variantClass, className]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      ref={buttonRef}
      type={type} // NEW
      onClick={onClick}
      disabled={disabled}
      className={buttonClassName}
      style={{
        ...(variant === "primary" ? styles.buttonPrimary : styles.buttonSecondary),
        ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}),
        ...style,
      }}
    >
      {children}
    </button>
  );
};

// ─── SearchInput ──────────────────────────────────────────────────────────────
export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "style"> {
  placeholder?: string;
  containerStyle?: CSSProperties;
  style?: CSSProperties;
  variant?: "default" | "primary";
}
export const SearchInput: React.FC<SearchInputProps> = ({
  placeholder = "Search…",
  containerStyle,
  style,
  variant = "default",
  className,
  ...props
}) => (
  <div
    className={`ps-search-shell ${variant === "primary" ? "ps-search-shell--primary" : ""}`}
    style={containerStyle}
  >
    {variant === "primary" && (
      <span className="ps-search-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
          <line x1="16.65" y1="16.65" x2="21" y2="21" stroke="currentColor" strokeWidth="2" />
        </svg>
      </span>
    )}
    <input
      {...props}
      className={["ps-search-input", variant === "primary" ? "ps-search-input--primary" : "", className]
        .filter(Boolean)
        .join(" ")}
      placeholder={placeholder}
      style={{ ...styles.searchInput, ...style }}
    />
  </div>
);

// ─── Header ───────────────────────────────────────────────────────────────────
export interface HeaderProps {
  children: ReactNode;
  level?: 1 | 2 | 3 | 4;
  style?: CSSProperties;
}
export const Header: React.FC<HeaderProps> = ({ children, level = 2, style }) => {
  const Tag = ("h" + level) as unknown as ElementType;
  const fontSizes: Record<number, string> = {
    1: "1.75rem",
    2: "1.5rem",
    3: "1.25rem",
    4: "1rem",
  };
  const levelStyle: CSSProperties =
    level === 1
      ? {
          margin: "0 0 14px",
          fontWeight: 700,
          lineHeight: 1.2,
          display: "block",
          width: "100%",
          borderBottom: `5px solid var(--ps-section-accent, ${theme.colors.primary})`,
          paddingBottom: 6,
        }
      : {};
  return (
    <Tag style={{ ...styles.header, ...levelStyle, fontSize: fontSizes[level], ...style }}>
      {children}
    </Tag>
  );
};

// ─── Card ─────────────────────────────────────────────────────────────────────
export interface CardProps {
  children: ReactNode;
  style?: CSSProperties;
}
export const Card: React.FC<CardProps> = ({ children, style }) => (
  <div className="ps-card" style={{ ...styles.card, ...style }}>
    {children}
  </div>
);

// ─── List ─────────────────────────────────────────────────────────────────────
export interface ListProps {
  items: string[];
  style?: CSSProperties;
}
export const List: React.FC<ListProps> = ({ items, style }) => (
  <ul style={{ ...styles.list, ...style }}>
    {items.map((item, i) => (
      <li key={i} style={styles.listItem}>
        {item}
      </li>
    ))}
  </ul>
);

// ─── StatBubble ───────────────────────────────────────────────────────────────
export interface StatBubbleProps {
  label: string;
  value: number | string;
  style?: CSSProperties;
  children?: React.ReactNode;
}
export const StatBubble: React.FC<StatBubbleProps> = ({ label, value, style, children }) => (
  <div style={{ ...styles.statBubble, ...style }}>
    <div style={styles.statValue}>{value}</div>
    <div style={styles.statLabel}>{label}</div>
    {children}
  </div>
);

// ─── InputText ─────────────────────────────────────────────────────────────────
// Now forwards refs so you can focus(), select(), etc. from parent components.
export interface InputTextProps extends InputHTMLAttributes<HTMLInputElement> {
  style?: CSSProperties;
}
export const InputText = forwardRef<HTMLInputElement, InputTextProps>(
  ({ style, ...props }, ref) => (
    <input ref={ref} {...props} style={{ ...styles.inputBase, ...style }} />
  )
);
InputText.displayName = "InputText";

// ─── SelectInput ───────────────────────────────────────────────────────────────
export interface SelectInputProps
  extends SelectHTMLAttributes<HTMLSelectElement> {
  style?: CSSProperties;
}
export const SelectInput: React.FC<SelectInputProps> = ({
  style,
  children,
  ...props
}) => (
  <select {...props} style={{ ...styles.inputBase, ...style }}>
    {children}
  </select>
);

// ─── TextArea ──────────────────────────────────────────────────────────────────
export interface TextAreaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  style?: CSSProperties;
}
export const TextArea: React.FC<TextAreaProps> = ({ style, ...props }) => (
  <textarea {...props} style={{ ...styles.inputBase, minHeight: 80, ...style }} />
);

// ─── FormField ─────────────────────────────────────────────────────────────────
export interface FormFieldProps {
  label: string;
  children: ReactNode;
  style?: CSSProperties;
}
export const FormField: React.FC<FormFieldProps> = ({ label, children, style }) => (
  <div style={{ ...styles.fieldWrapper, ...style }}>
    <label style={styles.fieldLabel}>{label}</label>
    {children}
  </div>
);

// ─── MetricCard ────────────────────────────────────────────────────────────────
export type MetricCardVariant =
  | "primary"
  | "info"
  | "warning"
  | "surface"
  | "success";

export interface MetricCardProps {
  label: string;
  value: ReactNode;
  helperText?: string;
  rightLabel?: string;
  icon?: ReactNode;
  variant?: MetricCardVariant;
  style?: CSSProperties;
}

const metricVariantBackground: Record<MetricCardVariant, string> = {
  primary: "linear-gradient(135deg, #7367F0, #A78BFA)",
  info: "linear-gradient(135deg, #26A4FF, #60A5FA)",
  warning: "linear-gradient(135deg, #FFB547, #F97316)",
  surface: theme.colors.surface,
  success: "linear-gradient(135deg, #10B981, #34D399)",
};

const metricVariantTextColor: Record<MetricCardVariant, string> = {
  primary: "#FFFFFF",
  info: "#FFFFFF",
  warning: "#111827",
  surface: theme.colors.text,
  success: "#FFFFFF",
};

export const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  helperText,
  rightLabel,
  icon,
  variant = "primary",
  style,
}) => {
  const isSurface = variant === "surface";
  return (
    <div
      style={{
        ...styles.metricCard,
        background: metricVariantBackground[variant],
        color: metricVariantTextColor[variant],
        boxShadow: isSurface ? "0 12px 30px rgba(15, 23, 42, 0.04)" : styles.metricCard.boxShadow,
        border: isSurface ? `1px solid ${theme.colors.secondaryBorder}` : "none",
        ...style,
      }}
    >
      <div style={styles.metricCardHeader}>
        <div>
          <div style={styles.metricLabel}>{label}</div>
          <div style={styles.metricValue}>{value}</div>
        </div>
        {(rightLabel || icon) && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            {rightLabel && <div style={styles.metricRightLabel}>{rightLabel}</div>}
            {icon && <div style={styles.metricIconBadge}>{icon}</div>}
          </div>
        )}
      </div>
      {helperText && <div style={styles.metricHelper}>{helperText}</div>}
    </div>
  );
};

// ─── ListPanel ─────────────────────────────────────────────────────────────────
export type ListPanelTrend = "up" | "down" | "neutral";

export interface ListPanelItem {
  id: string | number;
  primary: ReactNode;
  secondary?: ReactNode;
  meta?: ReactNode;
  trend?: ListPanelTrend;
}

export interface ListPanelProps {
  title: string;
  items: ListPanelItem[];
  style?: CSSProperties;
}

export const ListPanel: React.FC<ListPanelProps> = ({ title, items, style }) => {
  return (
    <div style={{ ...styles.listPanel, ...style }}>
      <div style={styles.listPanelHeader}>
        <div style={styles.listPanelTitle}>{title}</div>
      </div>
      <div style={styles.listPanelBody}>
        {items.length === 0 ? (
          <div style={styles.listPanelEmpty}>No data yet.</div>
        ) : (
          items.map((item) => {
            const trendStyle =
              item.trend === "up"
                ? styles.listPanelRightPositive
                : item.trend === "down"
                ? styles.listPanelRightNegative
                : undefined;
            return (
              <div key={item.id} style={styles.listPanelRow}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.listPanelPrimary}>{item.primary}</div>
                  {item.secondary && <div style={styles.listPanelSecondary}>{item.secondary}</div>}
                </div>
                {item.meta && (
                  <div style={{ ...styles.listPanelMeta, ...(trendStyle || {}) }}>{item.meta}</div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
